import type { LiveListing, Product, Sale } from "@/context/AppContext";
import {
  type BundleConfidence,
  type BundleSellSpeed,
  type BundleSortMode,
  type BundleStrategyId,
  type BundleSuggestion,
} from "@/features/bundleEngine";
import {
  computeBundlePerformance,
  type BundlePerformanceStat,
} from "@/features/bundlePerformance";
import {
  computeStrategySellSpeed,
  getStrategySpeed,
} from "@/features/sellThrough";

// Confidence band thresholds: downgrade if ROI std dev (percentage points) is high.
const HIGH_VARIANCE_DOWNGRADE = 50; // for n<5
const MED_VARIANCE_DOWNGRADE = 30; // for n>=5

function deriveConfidence(
  sampleSize: number,
  perf: BundlePerformanceStat | undefined,
): BundleConfidence {
  if (sampleSize === 0) return "Unknown";
  const stdDev = perf?.roiStdDev ?? null;
  if (sampleSize < 5) {
    if (stdDev != null && stdDev > HIGH_VARIANCE_DOWNGRADE) return "Low";
    return "Medium";
  }
  // n >= 5
  if (stdDev != null && stdDev > MED_VARIANCE_DOWNGRADE) return "Medium";
  return "High";
}

export interface EnrichedContext {
  products: Product[];
  liveListings: LiveListing[];
  sales: Sale[];
}

/**
 * Populates sellSpeed, confidence, sample size, and avg days-to-sell on each suggestion,
 * using historical data keyed by (strategyId, productType) for speed and by strategy for confidence.
 */
export function enrichBundleSuggestions(
  suggestions: BundleSuggestion[],
  ctx: EnrichedContext,
): BundleSuggestion[] {
  const speedMap = computeStrategySellSpeed(
    ctx.products,
    ctx.liveListings,
    ctx.sales,
  );
  const perfStats = computeBundlePerformance(ctx.products, ctx.sales);
  const perfByStrategy = new Map<BundleStrategyId, BundlePerformanceStat>();
  for (const stat of perfStats) perfByStrategy.set(stat.strategyId, stat);

  return suggestions.map((s): BundleSuggestion => {
    const speedInfo = getStrategySpeed(
      speedMap,
      s.strategyId,
      s.suggestedProductType,
    );
    const perf = perfByStrategy.get(s.strategyId);
    const sellSpeed: BundleSellSpeed = speedInfo.speed;
    return {
      ...s,
      sellSpeed,
      historicalSampleSize: speedInfo.sampleSize,
      historicalAvgDaysToSell: speedInfo.avgDaysToSell,
      confidence: deriveConfidence(speedInfo.sampleSize, perf),
    };
  });
}

// ─── Sorting ───────────────────────────────────────────────────────────────────

const SPEED_BUCKET_ORDER: Record<BundleSellSpeed, number> = {
  Fast: 0,
  Medium: 1,
  Slow: 2,
  Unknown: 3,
};

const SPEED_WEIGHT: Record<BundleSellSpeed, number> = {
  Fast: 1.0,
  Medium: 0.7,
  Slow: 0.4,
  Unknown: 0.6,
};

const CONFIDENCE_WEIGHT: Record<BundleConfidence, number> = {
  High: 1.0,
  Medium: 0.7,
  Low: 0.4,
  // "Unknown" gets a neutral mid weight so brand-new strategies aren't shoved to the bottom in Balanced mode.
  Unknown: 0.6,
};

export function balancedScore(s: BundleSuggestion): number {
  // For negative profit, multiplying by a sub-1 weight would *shrink* the loss and
  // perversely promote larger losses above smaller ones. Apply weights to positive
  // profits only; for losses, use raw profit so smaller losses always rank higher.
  const sw = SPEED_WEIGHT[s.sellSpeed];
  const cw = CONFIDENCE_WEIGHT[s.confidence];
  return s.expectedProfit >= 0 ? s.expectedProfit * sw * cw : s.expectedProfit;
}

/**
 * Sort enriched suggestions by the chosen mode.
 * - "profit": items with no historical data (sampleSize === 0) are pushed to the bottom, then sorted by expected profit desc.
 * - "speed": ordered by speed bucket (Fast → Medium → Slow → Unknown), then by avgDaysToSell asc; Unknown items naturally land at the bottom.
 * - "balanced": pure balanced-score sort (profit × speedWeight × confidenceWeight). No-history items are NOT forced to the bottom — Unknown gets a neutral mid weight.
 */
export function rankBundles(
  suggestions: BundleSuggestion[],
  mode: BundleSortMode,
): BundleSuggestion[] {
  const arr = [...suggestions];
  if (mode === "profit") {
    arr.sort((a, b) => {
      const aHas = a.historicalSampleSize > 0 ? 1 : 0;
      const bHas = b.historicalSampleSize > 0 ? 1 : 0;
      if (aHas !== bHas) return bHas - aHas;
      return b.expectedProfit - a.expectedProfit;
    });
    return arr;
  }
  if (mode === "speed") {
    arr.sort((a, b) => {
      const ab = SPEED_BUCKET_ORDER[a.sellSpeed];
      const bb = SPEED_BUCKET_ORDER[b.sellSpeed];
      if (ab !== bb) return ab - bb;
      const aDays = a.historicalAvgDaysToSell ?? Number.POSITIVE_INFINITY;
      const bDays = b.historicalAvgDaysToSell ?? Number.POSITIVE_INFINITY;
      if (aDays !== bDays) return aDays - bDays;
      return b.expectedProfit - a.expectedProfit;
    });
    return arr;
  }
  // balanced — pure balanced score; do NOT force no-history items to the bottom (only Profit / Speed do that).
  arr.sort((a, b) => balancedScore(b) - balancedScore(a));
  return arr;
}

export function confidenceColor(
  c: BundleConfidence,
  colors: { success?: string; secondary: string; mutedForeground: string },
): string {
  if (c === "High") return colors.success ?? "#27ae60";
  if (c === "Medium") return colors.secondary;
  // Low and Unknown both render in muted foreground.
  return colors.mutedForeground;
}
